import { Box, Typography, Chip, Avatar } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { motion } from 'framer-motion';
import { useReducedMotion } from '../../../../hooks/useReducedMotion';
import { reducedMotionVariants } from '../../../../theme/animations';

interface SectionHeaderProps {
  title: string;
  completed: boolean;
  summary?: string;
  expanded: boolean;
  stepNumber?: number;
  subtitle?: string;
  optional?: boolean;
}

const checkmarkVariants = {
  hidden: { scale: 0, rotate: -180, opacity: 0 },
  visible: {
    scale: 1,
    rotate: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 260,
      damping: 20,
      delay: 0.1,
    },
  },
};

const iconPulseVariants = {
  pulse: {
    scale: [1, 1.1, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

export function SectionHeader({
  title,
  completed,
  summary,
  expanded,
  stepNumber,
  subtitle,
  optional,
}: SectionHeaderProps) {
  const reducedMotion = useReducedMotion();

  // Determine avatar styles based on state
  const getAvatarSx = () => {
    if (completed) {
      return {
        width: { xs: 24, sm: 28 },
        height: { xs: 24, sm: 28 },
        fontSize: { xs: 11, sm: 13 },
        bgcolor: 'success.main',
        color: 'white',
      };
    }
    if (expanded) {
      return {
        width: { xs: 24, sm: 28 },
        height: { xs: 24, sm: 28 },
        fontSize: { xs: 11, sm: 13 },
        bgcolor: 'primary.main',
        color: 'white',
      };
    }
    return {
      width: { xs: 24, sm: 28 },
      height: { xs: 24, sm: 28 },
      fontSize: { xs: 11, sm: 13 },
      bgcolor: 'transparent',
      color: 'grey.500',
      border: '2px solid',
      borderColor: 'grey.400',
    };
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
      {/* Icon / Step Number with animations */}
      <motion.div
        variants={
          reducedMotion ? reducedMotionVariants : completed ? checkmarkVariants : iconPulseVariants
        }
        initial={completed && !reducedMotion ? 'hidden' : false}
        animate={completed ? 'visible' : expanded && !reducedMotion ? 'pulse' : false}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {stepNumber !== undefined ? (
          <Avatar sx={getAvatarSx()}>
            {completed ? <CheckIcon sx={{ fontSize: 16 }} /> : stepNumber}
          </Avatar>
        ) : completed ? (
          <CheckCircleIcon
            sx={{
              color: 'success.main',
              fontSize: 32,
              filter: 'drop-shadow(0 2px 4px rgba(16,185,129,0.3))',
            }}
          />
        ) : (
          <RadioButtonUncheckedIcon
            sx={{
              fontSize: 32,
              color: expanded ? 'primary.main' : 'action.disabled',
              transition: 'color 0.3s ease',
            }}
          />
        )}
      </motion.div>

      {/* Title + optional subtitle */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: 18,
              color: expanded ? 'primary.main' : completed ? 'text.primary' : 'text.secondary',
              transition: 'color 0.3s ease',
            }}
          >
            {title}
          </Typography>
          {optional && (
            <Chip
              label="Optional"
              size="small"
              variant="outlined"
              sx={{ ml: 1, height: 20, fontSize: 10 }}
            />
          )}
        </Box>
        {subtitle && !expanded && !completed && (
          <Typography variant="caption" color="text.secondary" display="block">
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Status badge */}
      {expanded && !completed && (
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          <Chip
            label="In Progress"
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
              color: 'white',
              fontWeight: 600,
              fontSize: 11,
              height: 24,
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': {
                  boxShadow: '0 0 0 0 rgba(99,102,241,0.4)',
                },
                '50%': {
                  boxShadow: '0 0 0 4px rgba(99,102,241,0)',
                },
              },
            }}
          />
        </motion.div>
      )}

      {/* Summary text for completed sections */}
      {completed && summary && !expanded && (
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mr: 2,
              maxWidth: 300,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {summary}
          </Typography>
        </motion.div>
      )}

      {/* Expand icon with rotation */}
      <motion.div
        animate={{ rotate: expanded ? 180 : 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        <ExpandMoreIcon
          sx={{
            fontSize: 28,
            color: 'action.active',
          }}
        />
      </motion.div>
    </Box>
  );
}
